import React, { useMemo } from "react";
import {
  dayLong,
  isPastDay,
  isSameDay,
  weekOf,
  weekdayShort,
} from "../myTask";

export interface MobileTaskDaysProps {
  /** The day the screen is looking at. Its week is the one drawn. */
  selected: Date;
  onSelect: (day: Date) => void;
}

/**
 * The week across the top of My task, as a phone draws it: seven blocks, the
 * weekday over the date.
 *
 * The same week `TaskDayStrip` draws and a different shape of control, because
 * the desktop pill cannot be narrowed into this. That one is a row of labels —
 * "Thu · 26 Sep" — laid out along a line with a rule and a calendar button at
 * the end of it, and seven of those on a 360px screen come to about 45px each
 * with three words to fit in. So the phone stacks the two facts instead: the
 * weekday small and grey on the first line, the date alone and bold on the
 * second, with the month left to the heading of the day and to the picker.
 *
 * A grid rather than a flex row, so the seven are exactly equal and exactly
 * fill the width. A row of flexed pills each sized to its own text gives a
 * "Wed 25" a wider block than a "Fri 27", which on a strip the eye reads
 * across as a ruler is the one thing it must not do.
 *
 * No calendar button on the end of it. On a phone that one has moved up beside
 * the screen's title, where the row heading the screen is the row with space on
 * it — see `MyTaskPage`.
 *
 * The three states are the desktop strip's own, in the same three fills: the
 * chosen day in the brand's wash behind a brand border, a day that has gone
 * filled grey because nothing can be booked into it, and a day still ahead
 * white on a hairline. What is different is the ink on the date: on a block
 * this small the number is the thing being read, so it carries the state and
 * the weekday above it stays quiet.
 */
const MobileTaskDays: React.FC<MobileTaskDaysProps> = ({
  selected,
  onSelect,
}) => {
  const week = useMemo(() => weekOf(selected), [selected]);
  const today = useMemo(() => new Date(), []);

  return (
    <ul className="grid grid-cols-7 gap-1.5">
      {week.map((day) => {
        const active = isSameDay(day, selected);
        const isToday = isSameDay(day, today);
        const past = isPastDay(day, today);

        return (
          <li key={day.getTime()}>
            <button
              type="button"
              aria-pressed={active}
              // The block reads "Thu" over "26", which a screen reader would
              // run together as one word. The whole day is said properly here
              // instead, exactly as the desktop strip says it.
              aria-label={dayLong(day)}
              onClick={() => onSelect(day)}
              // Taller than it is wide and the full width of its column, so
              // the seven read as one ruler and each of them is a target a
              // thumb can land in without aiming.
              //
              // The air between the weekday and the date is what tells the two
              // apart. Set tight they read as one stacked label — "Wed14" — and
              // the date, which is the fact the eye is actually scanning for
              // along the row, loses its own line. The block grows with the gap
              // rather than squeezing it, so the number keeps the room it had.
              className={`flex h-[68px] w-full flex-col items-center justify-center gap-1.5 rounded-[8px] border transition-colors touch-manipulation [-webkit-tap-highlight-color:transparent] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40 ${
                active
                  ? "border-brand bg-[#F2FCFD]"
                  : past
                  ? "border-transparent bg-[#E8EBEE]"
                  : "border-line bg-white"
              }`}
            >
              <span
                className={`text-[12px] leading-none ${
                  active ? "text-[#00A5B4]" : "text-ink-muted"
                }`}
              >
                {weekdayShort(day)}
              </span>
              <span
                className={`text-[17px] font-semibold leading-tight ${
                  active
                    ? "text-[#00A5B4]"
                    : past
                    ? "text-ink-muted"
                    : "text-ink"
                }`}
              >
                {day.getDate()}
              </span>
              {/* Today, where today is not what is being looked at. Without it
                  a strip walked a fortnight out looks exactly like this week,
                  and on a phone there is no year on any block to say
                  otherwise. A dot rather than a word: there is no room for one,
                  and the screen reader is told in the label above. */}
              {isToday && !active && (
                <>
                  <span className="sr-only"> (today)</span>
                  <span
                    aria-hidden="true"
                    className="h-1 w-1 flex-shrink-0 rounded-full bg-brand"
                  />
                </>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default MobileTaskDays;
